import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import { styled } from '@mui/material/styles';
import Typography, { type TypographyProps } from '@mui/material/Typography';
import { noop } from '@openx/utils/noop';
import { type JSX, type ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useInRouterContext } from 'react-router';
import { useTargetingDisabledState } from '../../../utils/hooks/useTargetingDisabledState';
import { ChangesFromInfoTag } from '../ChangesFromInfoTag';
import { DisabledReasonMessageTooltipWrapper } from '../DisabledReasonMessageTooltip/DisabledReasonMessageTooltipWrapper';
import { CategoryActionButton } from './components/CategoryActionButton';
import { Description } from './components/Description';
import { TargetingContainer } from './components/TargetingContainer';

export const StyledFilterDimensionTypography = styled(Typography)`
  margin-left: ${({ theme }) => theme.spacing(3)};
  && {
    font-size: 13px;
    line-height: 28px;
  }
`;

export const StyledTargetingParameterLabelTypography = styled(Typography)`
  && {
    font-size: 13px;
    line-height: 28px;
  }
`;

type TargetingReadOnlyProps = {
  name: string | JSX.Element;
  field?: string;
  userChangedInitialValue?: boolean;
  isEmpty?: boolean;
  onOpenDrawerClick?: () => void;
  onRemove?: () => void;
  children?: ReactNode;
  readonly?: boolean;
  dataTest?: string;
  isDisabled?: boolean;
  disabledReasonMessage?: string;
  isRequired?: boolean;
  description?: string;
  isSingleChoice?: boolean;
  optionsCount?: number;
  isOpen?: boolean;
  toggleOpen?: () => void;
  hasError?: boolean;
  actionButtonOverride?: ReactNode;
  titleAdornment?: ReactNode;
  addButtonPendoFeature?: string;
  pendoFeature?: string;
};

export const TargetingReadOnly = ({
  name,
  field,
  userChangedInitialValue = false,
  isEmpty = true,
  onOpenDrawerClick = noop,
  children,
  readonly = false,
  dataTest,
  isDisabled = false,
  disabledReasonMessage,
  isRequired = false,
  description,
  isSingleChoice = false,
  optionsCount,
  isOpen = false,
  toggleOpen = noop,
  hasError = false,
  actionButtonOverride,
  titleAdornment,
  addButtonPendoFeature,
  pendoFeature,
}: TargetingReadOnlyProps) => {
  const { t } = useTranslation();

  const {
    isDisabledOrHasError: isFieldDisabledOrHasError,
    disabledReasonMessage: fieldDisabledReasonMessage,
    hasError: hasFieldError,
  } = useTargetingDisabledState(field);

  const isDisabledOrHasError = isDisabled || isFieldDisabledOrHasError;
  const effectiveDisabledReasonMessage = disabledReasonMessage ?? fieldDisabledReasonMessage;

  const isRouterContext = useInRouterContext();
  const hasOptionsSelected = !!optionsCount && optionsCount > 0;
  const showSingleChoiceAddButton = isSingleChoice && !hasOptionsSelected;

  const isExpandable = !showSingleChoiceAddButton;

  const headerColor = useMemo<TypographyProps['color']>(() => {
    if (isDisabledOrHasError) {
      return hasOptionsSelected ? 'error' : 'textDisabled';
    }

    return hasOptionsSelected ? 'success' : undefined;
  }, [isDisabledOrHasError, hasOptionsSelected]);

  const hasTargetingError = hasError || hasFieldError;

  if (!readonly)
    return (
      <TargetingContainer
        dataTest={dataTest}
        name={name}
        isRequired={isRequired}
        hasError={hasTargetingError}
        pendoFeature={pendoFeature}
      >
        <DisabledReasonMessageTooltipWrapper
          isDisabled={isDisabledOrHasError}
          disabledReasonMessage={effectiveDisabledReasonMessage}
        >
          <Grid
            container
            columnSpacing={2}
            onClick={isExpandable ? toggleOpen : undefined}
            data-test="targeting-readonly-clickable-container"
            sx={{
              justifyContent: 'space-between',
              alignItems: 'center',
              py: 2,
              px: 3,
              cursor: isExpandable ? 'pointer' : 'default',
            }}
          >
            <Grid
              container
              size="grow"
              sx={{
                flexDirection: 'column',
                alignItems: 'flex-start',
              }}
            >
              <Grid container>
                <Typography variant="h3" color={headerColor}>
                  {name}
                  {hasOptionsSelected && ` [ ${optionsCount} ]`}
                </Typography>
                {titleAdornment}

                {isRouterContext && typeof field === 'string' && !userChangedInitialValue && (
                  <ChangesFromInfoTag fieldName={field} />
                )}
              </Grid>
              {!readonly && <Description>{description}</Description>}
            </Grid>

            <Grid>
              {actionButtonOverride ?? (
                <CategoryActionButton
                  isDisabled={isDisabledOrHasError}
                  showSingleChoiceAddButton={showSingleChoiceAddButton}
                  onOpenDrawerClick={onOpenDrawerClick}
                  isOpen={isOpen}
                  toggleOpen={toggleOpen}
                  addButtonPendoFeature={addButtonPendoFeature}
                />
              )}
            </Grid>
          </Grid>
        </DisabledReasonMessageTooltipWrapper>

        {isOpen && (
          <Box
            sx={theme => ({
              padding: theme.spacing(0, 3, showSingleChoiceAddButton ? 0 : 2, 3),
            })}
          >
            {children}
          </Box>
        )}
      </TargetingContainer>
    );

  return (
    <TargetingContainer
      dataTest={dataTest}
      name={name}
      isRequired={isRequired}
      hasError={hasTargetingError}
      pendoFeature={pendoFeature}
      sx={{
        padding: theme => theme.spacing(2, 3),
      }}
    >
      <Grid
        container
        columnSpacing={2}
        sx={{
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Grid
          container
          size="grow"
          sx={{
            flexDirection: 'column',
            alignItems: 'flex-start',
          }}
        >
          <Grid
            container
            sx={{
              alignItems: 'center',
              flexWrap: 'wrap',
              columnGap: 1.3,
              rowGap: 0.5,
            }}
          >
            <Typography variant="h3" component="span">
              {name}
            </Typography>
            {titleAdornment}

            {isEmpty && (
              <StyledFilterDimensionTypography variant="body1" color="textSecondary">
                {t('Any')}
              </StyledFilterDimensionTypography>
            )}

            {isRouterContext && typeof field === 'string' && !userChangedInitialValue && (
              <ChangesFromInfoTag fieldName={field} />
            )}
          </Grid>
        </Grid>
      </Grid>

      {children}
    </TargetingContainer>
  );
};
